import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

import divisionsService from '../../service/api/DivisionsService';

import animalsService from '../../service/api/AnimalsService';
import { Dialog } from 'primereact/dialog';
import AnimalDialog from './AnimalDialog';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { SETTINGS } from '../../enums/settings.enum';
import { Animal } from '../../types/Animal';
import WriteOnlyButton from '../controls/WriteOnlyButton';

export const AnimalManagement = () => {
  const toast = useRef<any>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [animals, setAnimals] = useState<Animal[]>(null);
  const [animalToDelete, setAnimalToDelete] = useState<Animal>();

  const [divisions, setDivisions] = useState();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Animal>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();

  const showSaveToast = (hostName: string) => {
    toast.current.show({
      severity: 'success',
      summary: hostName,
      detail: t('animals.toast_saved'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const showAddToast = (hostName: string) => {
    toast.current.show({
      severity: 'success',
      summary: hostName,
      detail: t('animals.toast_added'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const showDeleteToast = (hostName: string) => {
    toast.current.show({
      severity: 'info',
      summary: hostName,
      detail: t('animals.toast_deleted'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const loadData = () => {
    setLoading(true);
    Promise.all([animalsService.getAll(), divisionsService.getAll()]).then((response) => {
      setDivisions(response[1]);
      const divisionsDict = Object.assign({}, ...response[1].map((r) => ({ [r.id]: r.name })));

      const _animals = response[0];
      _animals.forEach((_x) => {
        _x.divisionName = divisionsDict[_x.divisionId];
      });
      setAnimals(_animals);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const animalsTableHeader = (
    <div className="table-header">
      {t('animals.header')}
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addAnimal();
          }}
        ></Button>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    loadData();
  };

  const confirmDeleteAnimal = (animal: Animal) => {
    setAnimalToDelete(animal);
    setDeleteDialogVisible(true);
  };

  const deleteAnimal = () => {
    setLoading(true);
    animalsService.delete(animalToDelete.id).then(() => {
      loadData();
      showDeleteToast(animalToDelete.name);
    });

    setDeleteDialogVisible(false);
  };
  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editAnimal = (_animal: Animal) => {
    setSelectedRowToEdit(_animal);
    setAddEditDialogVisible(true);
  };

  const addAnimal = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const viewDetails = (id: string) => {
    history.push(`/animal-details/${id}`);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button label={t('common.yes')} icon="pi pi-check" className="p-button-text" onClick={() => deleteAnimal()} />
    </>
  );

  const bodyTemplate = (data: Animal, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const actionTemplate = (data: Animal) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editAnimal(data);
        }}
      ></Button>
      <WriteOnlyButton
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          confirmDeleteAnimal(data);
        }}
      />
      <Button
        type="button"
        icon="pi pi-search"
        tooltip={t('common.details')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        onClick={() => {
          viewDetails(data.id);
        }}
      ></Button>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={animals}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('animals.empty_message')}
            loading={loading}
            header={animalsTableHeader}
          >
            <Column field="name" header={t('common.name')} sortable body={bodyTemplate}></Column>
            <Column field="description" header={t('animals.description')} sortable body={bodyTemplate}></Column>
            <Column field="rfid" header={t('animals.rfid')} sortable body={bodyTemplate}></Column>
            <Column field="divisionName" header={t('animals.division')} sortable body={bodyTemplate}></Column>
            <Column
              headerStyle={{ width: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <AnimalDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            animal={selectedRowToEdit}
            availableDivisions={divisions}
            showAddToast={showAddToast}
            showSaveToast={showSaveToast}
          />
          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {animalToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: animalToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  );
};
